import { AccountGroup } from "@common/types/domains/accounting/account";
import type { PickerItemData } from "@design-system/Picker/Picker.types";

/**
 * Build hierarchical Picker tree items from a flat list of account groups.
 *
 * When `excludeIds` is provided, every group in `excludeIds` AND every
 * descendant of those groups is omitted from the resulting tree. This is the
 * correct behavior for parent-pickers (e.g. GroupSelectField) where excluding
 * "self" must also exclude self's subtree to prevent cycles. Without this
 * closure, descendants of an excluded id would silently re-root to the top
 * level (because their parent id is no longer present in the filtered set).
 */
export function buildGroupTree(
    groups: AccountGroup[],
    excludeIds?: readonly string[],
    disabledReasonByGroupId?: ReadonlyMap<string, string>,
): Array<PickerItemData<string>> {
    const exclusionClosure = computeExclusionClosure(groups, excludeIds ?? []);
    const visibleGroups =
        exclusionClosure.size === 0
            ? groups
            : groups.filter((group) => !exclusionClosure.has(group.id));

    const childrenByParent = new Map<string | null, AccountGroup[]>();
    const presentIds = new Set(visibleGroups.map((group) => group.id));

    for (const group of visibleGroups) {
        const key =
            group.parentGroupId !== null && presentIds.has(group.parentGroupId)
                ? group.parentGroupId
                : null;
        const list = childrenByParent.get(key) ?? [];
        list.push(group);
        childrenByParent.set(key, list);
    }

    const sortByName = (a: AccountGroup, b: AccountGroup): number =>
        a.name.localeCompare(b.name);

    const buildNode = (group: AccountGroup): PickerItemData<string> => {
        const children = childrenByParent.get(group.id) ?? [];
        const sortedChildren = [...children].sort(sortByName);
        const disabledReason = disabledReasonByGroupId?.get(group.id);
        return {
            value: group.id,
            label: group.name,
            ...(disabledReason !== undefined && {
                disabled: true,
                disabledReason,
            }),
            ...(sortedChildren.length > 0 && {
                children: sortedChildren.map(buildNode),
            }),
        };
    };

    const roots = childrenByParent.get(null) ?? [];
    return [...roots].sort(sortByName).map(buildNode);
}

function computeExclusionClosure(
    groups: AccountGroup[],
    excludeIds: readonly string[],
): Set<string> {
    if (excludeIds.length === 0) {
        return new Set();
    }
    const childrenByParent = new Map<string, AccountGroup[]>();
    for (const group of groups) {
        if (group.parentGroupId === null) {
            continue;
        }
        const list = childrenByParent.get(group.parentGroupId) ?? [];
        list.push(group);
        childrenByParent.set(group.parentGroupId, list);
    }

    const closure = new Set<string>();
    const stack = [...excludeIds];
    while (stack.length > 0) {
        const id = stack.pop();
        if (id === undefined) {
            break;
        }
        if (closure.has(id)) {
            continue;
        }
        closure.add(id);
        const children = childrenByParent.get(id) ?? [];
        for (const child of children) {
            stack.push(child.id);
        }
    }
    return closure;
}
